import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import './Navbar.css';

const Navbar = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const auth = getAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setIsLoggedIn(!!user);
      setUserEmail(user?.email || '');
    });

    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);

    return () => {
      unsubscribe();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [auth]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileOpen(false);
  }, [location]);

  const handleLogout = () => {
    signOut(auth)
      .then(() => navigate('/login'))
      .catch((error) => console.error("Logout Error:", error));
  };

  const isActive = (path) => location.pathname === path;
  const isHomePage = location.pathname === '/';

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/dashboard', label: 'Dashboard' },
    { path: '/programs', label: 'Programs' },
    { path: '/quiz', label: 'Quiz' },
    { path: '/forum', label: 'Forum' },
    { path: '/fish-details', label: 'Explore' },
  ];

  return (
    <>
      <nav className={`navbar ${(isScrolled || !isHomePage) ? 'scrolled' : ''} ${isMobileOpen ? 'mobile-open' : ''}`}>
        <div className="nav-inner">
          {/* Logo */}
          <Link to="/" className="nav-brand">
            <div className="logo-mark">
              <svg width="28" height="28" viewBox="0 0 32 32" fill="none">
                <circle cx="16" cy="16" r="14" fill="url(#logoGrad)" />
                <path d="M8 18c2-3 4-4 8-4s6 1 8 4" stroke="white" strokeWidth="2.5" strokeLinecap="round" fill="none"/>
                <circle cx="12" cy="13" r="1.5" fill="white" />
                <circle cx="20" cy="13" r="1.5" fill="white" />
                <defs>
                  <linearGradient id="logoGrad" x1="0" y1="0" x2="32" y2="32">
                    <stop offset="0%" stopColor="#00b4d8"/>
                    <stop offset="100%" stopColor="#0077b6"/>
                  </linearGradient>
                </defs>
              </svg>
            </div>
            <span className="logo-text">OceanSense</span>
          </Link>

          {/* Center Nav Links */}
          <ul className={`nav-links ${isMobileOpen ? 'active' : ''}`}>
            {navLinks.map(link => (
              <li key={link.path}>
                <Link 
                  to={link.path} 
                  className={isActive(link.path) ? 'active' : ''}
                >
                  {link.label}
                </Link>
              </li>
            ))}
            
            <li className="nav-divider"></li>
            
            <li>
              <Link to="/report" className={`nav-report-link ${isActive('/report') ? 'active' : ''}`}>
                <span className="report-dot"></span>
                Report
              </Link>
            </li>
            <li>
              <Link to="/emergency" className={`nav-emergency-link ${isActive('/emergency') ? 'active' : ''}`}>
                Emergency
              </Link>
            </li>

            {/* Mobile-only auth */}
            <li className="mobile-auth">
              {isLoggedIn ? (
                <button className="nav-cta" onClick={handleLogout}>Logout</button>
              ) : (
                <Link to="/login" className="nav-cta">Login</Link>
              )}
            </li>
          </ul>

          {/* Right Side Actions */}
          <div className="nav-right">
            {isLoggedIn ? (
              <div className="user-area">
                <div className="user-avatar-pill">
                  <span className="avatar-letter">{userEmail.charAt(0).toUpperCase()}</span>
                  <span className="avatar-email">{userEmail.split('@')[0]}</span>
                </div>
                <button className="nav-cta logout-btn" onClick={handleLogout}>Logout</button>
              </div>
            ) : (
              <Link to="/login" className="nav-cta">
                Get Started
              </Link>
            )}
          </div>

          {/* Hamburger */}
          <button 
            className={`hamburger ${isMobileOpen ? 'open' : ''}`} 
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            aria-label="Toggle menu"
          >
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
      </nav>

      {/* Mobile overlay */}
      {isMobileOpen && <div className="nav-overlay" onClick={() => setIsMobileOpen(false)} />}
    </>
  );
};

export default Navbar;
